import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  Star,
  CheckCircle,
  ArrowRight,
  BookOpen,
  Palette,
  Target
} from 'lucide-react';

const GuideViewer = ({ guide, onBack }) => {
  if (!guide) return null;

  // Scroll to top when guide loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [guide]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-500/20 text-green-300';
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'advanced':
        return 'bg-red-500/20 text-red-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 mb-8 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Guides</span>
      </button>

      {/* Guide Header */}
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20 mb-8">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(guide.difficulty)}`}>
            {guide.difficulty}
          </span>
          <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm font-medium">
            {guide.toolUsed}
          </span>
          <div className="flex items-center space-x-1 text-gray-400">
            <Clock className="h-4 w-4" />
            <span className="text-sm">{guide.readTime}</span>
          </div>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {guide.title}
        </h1>
        <p className="text-xl text-gray-300">
          {guide.subtitle}
        </p>
      </div>

      {/* Guide Content */}
      <div className="space-y-8">
        {guide.sections.map((section, index) => (
          <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-4">
                {index + 1}
              </span>
              {section.title}
            </h2>
            <div 
              className="prose-guide max-w-none"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          </div>
        ))}
      </div>

      {/* CTA Section */}
      {guide.cta && (
        <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-8 border border-purple-500/30 mt-12">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              {guide.cta.title}
            </h3>
            <p className="text-xl text-gray-300 mb-8">
              {guide.cta.description}
            </p>
            <Link
              to={guide.cta.buttonLink}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 inline-flex items-center space-x-2"
            >
              <span>{guide.cta.buttonText}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      )}

      {/* Related Tools */}
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20 mt-8">
        <h3 className="text-xl font-bold text-white mb-6">Complete Your Publishing Workflow</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-purple-400 mb-3 flex justify-center">
              <BookOpen className="h-8 w-8" />
            </div>
            <h4 className="font-semibold text-white mb-2">AI Book Generator</h4>
            <p className="text-gray-400 text-sm mb-3">Create complete manuscripts in minutes</p>
            <Link to="/register" className="text-purple-400 hover:text-purple-300 text-sm font-medium">
              Try Now →
            </Link>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-purple-400 mb-3 flex justify-center">
              <Palette className="h-8 w-8" />
            </div>
            <h4 className="font-semibold text-white mb-2">Cover Generator</h4>
            <p className="text-gray-400 text-sm mb-3">KDP-compliant covers in 60 seconds</p>
            <Link to="/register" className="text-purple-400 hover:text-purple-300 text-sm font-medium">
              Try Now →
            </Link>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-4 text-center">
            <div className="text-purple-400 mb-3 flex justify-center">
              <Target className="h-8 w-8" />
            </div>
            <h4 className="font-semibold text-white mb-2">Metadata Optimizer</h4>
            <p className="text-gray-400 text-sm mb-3">AI-powered keyword research</p>
            <Link to="/register" className="text-purple-400 hover:text-purple-300 text-sm font-medium">
              Try Now →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideViewer;